import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CategoryService, Category, Subcategory as SubCategory, ProductCategory } from "@/lib/category-service";
import { MACHINERY_PRODUCTS, Product } from "@/polymet/data/machinery-products";
import BreadcrumbNavigation, {
  BreadcrumbItem,
} from "@/polymet/components/breadcrumb-navigation";
import ProductGrid from "@/polymet/components/product-grid";
import SearchFilters from "@/polymet/components/search-filters";

export default function ProductCategoryPage() {
  const {
    categoryId = "",
    subCategoryId = "",
    productCategoryId = "",
  } = useParams();

  const [category, setCategory] = useState<Category | null>(null);
  const [subCategory, setSubCategory] = useState<SubCategory | null>(null);
  const [productCategory, setProductCategory] =
    useState<ProductCategory | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Create an instance of CategoryService
    const categoryService = new CategoryService();
    
    // Create an async function to fetch data from Supabase
    const fetchCategoryData = async () => {
      try {
        // Fetch category by ID
        if (categoryId) {
          const foundCategory = await categoryService.getCategory(categoryId);
          if (foundCategory) {
            setCategory(foundCategory);
            
            // Fetch subcategory by ID
            if (subCategoryId) {
              const foundSubCategory = await categoryService.getSubcategory(subCategoryId);
              if (foundSubCategory) {
                setSubCategory(foundSubCategory);
                
                // Fetch product category by ID
                if (productCategoryId) {
                  const foundProductCategory = await categoryService.getProductCategory(productCategoryId);
                  if (foundProductCategory) {
                    setProductCategory(foundProductCategory);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching category data:', error);
      }
    };
    
    // Call the async function
    fetchCategoryData();

    // Filter products that match the product category
    const matchingProducts = MACHINERY_PRODUCTS.filter(
      (product) =>
        product.categoryId === categoryId &&
        product.subCategoryId === subCategoryId &&
        product.productCategoryId === productCategoryId
    );

    setProducts(matchingProducts);
    setFilteredProducts(matchingProducts);
  }, [categoryId, subCategoryId, productCategoryId]);

  const handleSearch = (filters: any) => {
    let filtered = [...products];

    // Apply filters
    if (filters.searchTerm) {
      const searchTerm = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm) ||
          product.description.toLowerCase().includes(searchTerm) ||
          product.manufacturer.toLowerCase().includes(searchTerm) ||
          product.model.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.priceRange && filters.priceRange.length === 2) {
      filtered = filtered.filter(
        (product) =>
          product.price >= filters.priceRange[0] &&
          product.price <= filters.priceRange[1]
      );
    }

    if (filters.selectedConditions && filters.selectedConditions.length > 0) {
      filtered = filtered.filter((product) =>
        filters.selectedConditions.includes(product.condition)
      );
    }

    if (
      filters.selectedManufacturers &&
      filters.selectedManufacturers.length > 0
    ) {
      filtered = filtered.filter((product) =>
        filters.selectedManufacturers.includes(product.manufacturer)
      );
    }

    if (filters.selectedYear) {
      if (filters.selectedYear === "Older") {
        filtered = filtered.filter(
          (product) => product.year && product.year < 2015
        );
      } else {
        const year = parseInt(filters.selectedYear);
        filtered = filtered.filter((product) => product.year === year);
      }
    }

    if (filters.location) {
      const location = filters.location.toLowerCase();
      filtered = filtered.filter((product) =>
        product.location.toLowerCase().includes(location)
      );
    }

    if (filters.availability && filters.availability.length > 0) {
      filtered = filtered.filter((product) => {
        return filters.availability.every((option: string) => {
          switch (option) {
            case "in-stock":
              return product.inStock;
            case "financing":
              return product.financing;
            case "delivery":
              return product.deliveryAvailable;
            default:
              return true;
          }
        });
      });
    }

    setFilteredProducts(filtered);
  };

  const handleResetFilters = () => {
    setFilteredProducts(products);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Create breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { name: "Home", href: "/" }
  ];
  
  if (category) {
    breadcrumbItems.push({
      name: category.name,
      href: `/category/${category.id}`,
    });
  }
  if (subCategory) {
    breadcrumbItems.push({
      name: subCategory.name,
      href: `/category/${categoryId}/subcategory/${subCategory.id}`,
    });
  }
  if (productCategory) {
    breadcrumbItems.push({
      name: productCategory.name,
      href: `/category/${categoryId}/subcategory/${subCategoryId}/product-category/${productCategory.id}`,
    });
  }

  if (!category || !subCategory || !productCategory) {
    return (
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-6">Category Not Found</h1>
        <p>The category you are looking for does not exist.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <BreadcrumbNavigation items={breadcrumbItems} className="mb-6" />

      <div className="flex flex-col md:flex-row gap-8">
        {/* Filters - Desktop */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <h2 className="text-xl font-bold mb-4">Filters</h2>
            <SearchFilters
              onSearch={handleSearch}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2">{productCategory.name}</h1>
            <p className="text-muted-foreground">
              {productCategory.description}
            </p>
          </div>

          {/* Filters - Mobile Toggle */}
          <div className="md:hidden mb-6">
            <button
              onClick={toggleFilters}
              className="w-full py-2 px-4 bg-muted rounded-md flex items-center justify-between"
            >
              <span className="font-medium">Filters</span>
              <span>{showFilters ? "Hide" : "Show"}</span>
            </button>

            {showFilters && (
              <div className="mt-4 p-4 border rounded-md">
                <SearchFilters
                  onSearch={handleSearch}
                  onReset={handleResetFilters}
                />
              </div>
            )}
          </div>

          <ProductGrid products={filteredProducts} showFilters={true} />
        </div>
      </div>
    </div>
  );
}
